import "@css/modal_setDeadLine.css";
import CustomDatepicker from "@components/customDatepicker/CustomDatepicker";
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import type * as type from "@vTypes";
import { useQueryClient } from "@tanstack/react-query";
import * as mutate from "@script/mutate";
import { useOutsideClick } from "@script/script";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth";
import { useGroupStore } from "@store/groupStore";
import { invalidateRosterCachesAndSelectLatestSchedule } from "@pages/roster-create/service/invalidateRosterCreateCaches";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

import { useScheduleStore } from "@store/scheduleStore.ts";

function SetDeadLine({
  wantedStatus,
  setIsOpendDeadlineModal,
  fetchWantedStatus,
  setIsOpendModalSubmittedWantedlistModal,
  targetRef,
  currentScheduleInfo,
  clearHistory,
}: {
  wantedStatus: type.WantedStatus;
  setIsOpendDeadlineModal: (value: boolean) => void;
  fetchWantedStatus: (year: number, month: number) => void;
  setIsOpendModalSubmittedWantedlistModal: (value: boolean) => void;
  targetRef?: React.RefObject<HTMLElement | null>;
  currentScheduleInfo?: { year: number; month: number; version: number };
  clearHistory?: () => void;
}) {
  const queryClient = useQueryClient();
  const { curDate, setCurScheduleInfo } = useScheduleStore();

  // 그룹 권위 소스(wanted 페이지와 동일): ADM=드롭다운 선택, HN/일반=현재 병동 토큰(group_id).
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();
  const { selectedGroupId } = useGroupStore();
  const gid = isAdmin ? (selectedGroupId ?? "") : (loginUser?.group_id ?? "");

  const year =
    currentScheduleInfo?.year ?? curDate?.year ?? new Date().getFullYear();
  const month =
    currentScheduleInfo?.month ?? curDate?.month ?? new Date().getMonth() + 1;

  const [deadlineDate, setDeadlineDate] = useState<Date | null>(
    wantedStatus.exp_date
  );
  const [currentDate, setCurrentDate] = useState<Date>(new Date());

  const [isEditable, setIsEditable] = useState<boolean>(!wantedStatus.status);
  // const [isDatepickerDisabled, setIsDatepickerDisabled] = useState<boolean>(false);

  const [modalStyle, setModalStyle] = useState<React.CSSProperties>({
    left: "45%",
    top: "90px",
    visibility: "hidden",
  });

  /* 드래그 기능 */
  const ref_modal_customDatePicker = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  const deadline_handleMouseDown = (e: React.MouseEvent) => {
    const modal = ref_modal_customDatePicker.current;
    const mainDom = document.querySelector(".c_main__container") as HTMLElement;
    if (!modal || !mainDom) return;

    const modalRect = modal.getBoundingClientRect();

    offsetRef.current = {
      x: e.clientX - modalRect.left,
      y: e.clientY - modalRect.top,
    };

    draggingRef.current = true;
  };
  const deadline_handleMouseMove = (e: MouseEvent) => {
    if (!draggingRef.current) return;

    const mainDom = document.querySelector(".c_main__container") as HTMLElement;
    const modal = ref_modal_customDatePicker.current;
    if (!mainDom || !modal) return;

    const mainRect = mainDom.getBoundingClientRect();
    const modalRect = modal.getBoundingClientRect();

    const rawX = e.clientX - mainRect.left - offsetRef.current.x;
    const rawY = e.clientY - mainRect.top - offsetRef.current.y;

    const clampedX = Math.max(
      0,
      Math.min(rawX, mainRect.width - modalRect.width)
    );
    const clampedY = Math.max(
      0,
      Math.min(rawY, mainRect.height - modalRect.height)
    );

    const xPercent = (clampedX / mainRect.width) * 100;
    const yPercent = (clampedY / mainRect.height) * 100;

    // setPosition({ x: xPercent, y: yPercent });
    modal.style.left = `${xPercent}%`;
    modal.style.top = `${yPercent}%`;
  };

  const deadline_handleMouseUp = () => {
    draggingRef.current = false;
  };

  /* 외부 클릭 */
  useOutsideClick(ref_modal_customDatePicker, () => {
    setIsOpendDeadlineModal(false);
  });

  useEffect(() => {
    window.addEventListener("mousemove", deadline_handleMouseMove);
    window.addEventListener("mouseup", deadline_handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", deadline_handleMouseMove);
      window.removeEventListener("mouseup", deadline_handleMouseUp);
    };
  }, []);

  useEffect(() => {
    setDeadlineDate(wantedStatus.exp_date);
    setIsEditable(!wantedStatus.status);
    setCurrentDate(
      wantedStatus.exp_date
        ? new Date(wantedStatus.exp_date)
        : new Date(year, month - 2)
    );
  }, [wantedStatus, year, month]);

  // 원티드 버튼 아래에 모달 위치하게 하기 위함.
  useLayoutEffect(() => {
    if (targetRef?.current && ref_modal_customDatePicker.current) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const modalRect =
        ref_modal_customDatePicker.current.getBoundingClientRect();
      const mainDom = document.querySelector(
        ".c_main__container"
      ) as HTMLElement;

      if (mainDom) {
        const mainRect = mainDom.getBoundingClientRect();

        // 버튼 바로 아래(bottom) + 10px 여백
        const top = targetRect.bottom - mainRect.top + 10;

        // 버튼 오른쪽 끝에 정렬
        let left =
          targetRect.left +
          targetRect.width / 2 -
          modalRect.width / 2 -
          mainRect.left;
        // 화면 밖으로 나가지 않도록 보정
        if (left < 0) left = 10;
        if (left + modalRect.width > mainRect.width)
          left = mainRect.width - modalRect.width - 10;

        setModalStyle({
          left: `${left}px`,
          top: `${top}px`,
          visibility: "visible",
        });
      } else {
        setModalStyle({ left: "45%", top: "90px", visibility: "visible" });
      }
    } else {
      setModalStyle({ left: "45%", top: "90px", visibility: "visible" });
    }
  }, []);

  return (
    <div
      className="setDeadLine_modal"
      ref={ref_modal_customDatePicker}
      style={modalStyle}
    >
      <div>
        <h2 className="setDeadLineTitle" onMouseDown={deadline_handleMouseDown}>
          원티드 제출 요청
        </h2>
        <button
          type="button"
          className="modal_button_close"
          onClick={() => setIsOpendDeadlineModal(false)}
        ></button>
      </div>
      <div className="calendarBody">
        <CustomDatepicker
          currentDate={currentDate}
          selectedDate={deadlineDate}
          setDate={setDeadlineDate}
          disabled={!isEditable}
        />
      </div>
      <div className="setDeadlineInputWrap">
        {/* <div>요청 마감일 : {wantedStatus.exp_date ? dateFormatting(new Date(wantedStatus.exp_date)) : "없음"}</div> */}
        <label className="comp_inputCheck">
          <input
            type="checkbox"
            checked={!deadlineDate}
            disabled={!isEditable}
            onChange={(e) => {
              const isChecked = e.currentTarget.checked;
              if (isChecked) setDeadlineDate(null);
            }}
          />
          <span className="com_inputCheck_checkIcon"></span>
          마감일 없음
        </label>
      </div>
      <div className="setDeadlineButtonGroup">
        <button
          type="button"
          className={`setDeadlineButton setDeadlineButton_showSubmitted ${wantedStatus?.status === null ? "setDeadlineButton_showSubmitted--disabled" : ""}`}
          disabled={wantedStatus?.status === null}
          onClick={() => {
            setIsOpendModalSubmittedWantedlistModal(true);
          }}
        >
          제출현황보기
        </button>
        {wantedStatus?.status !== "closed" && isEditable ? (
          <button
            type="button"
            className="setDeadlineButton setDeadlineButton_request"
            onClick={async () => {
              if (!confirm("요청하시겠습니까")) return;

              try {
                if (wantedStatus?.status === "requested") {
                  await mutate.saveChangeWantedDeadline(
                    year,
                    month,
                    deadlineDate,
                    gid
                  );
                  alert("변경되었습니다.");
                } else {
                  await mutate.requestWantedDeadline(
                    year,
                    month,
                    deadlineDate,
                    gid
                  );
                  alert("요청되었습니다.");
                }

                // 히스토리 초기화 (마감 저장 성공 시)
                clearHistory?.();

                fetchWantedStatus(year, month);

                // 알람 리스트 갱신
                await queryClient.invalidateQueries({
                  queryKey: QUERY_KEYS.alarms.list(),
                });

                // ✅ 성공 시에만 닫기
                setIsOpendDeadlineModal(false);

                // 근무표 생성 완료 시와 동일하게: 해당 월 캐시 무효화 + 최신 스케줄 선택.
                //   wanted.status 도 무효화되어 hasWanted("원티드 요청을 먼저해주세요")가 갱신됨.
                //   (같은 월 유지 — 비영속 store 라 하드 리로드는 월을 잃으므로 SPA 갱신)
                await invalidateRosterCachesAndSelectLatestSchedule(
                  queryClient,
                  { year, month, selectedGroupId: gid || null, isAdmin },
                  setCurScheduleInfo
                );
              } catch {
                alert("에러");
                // ❌ 에러 시 유지
              }
            }}
          >
            요청하기
          </button>
        ) : (
          <button
            type="button"
            className="setDeadlineButton setDeadlineButton_request"
            onClick={async () => {
              if (confirm("마감일을 변경하시겠습니까?")) {
                setIsEditable(true);
              }
            }}
          >
            마감일 수정
          </button>
        )}
      </div>
    </div>
  );
}

export default SetDeadLine;
